/**
 * SwapCard — the main swap surface.
 *
 * Renders the swap form, token inputs, slippage settings, and the
 * transaction status overlay. All swap lifecycle logic (approval,
 * simulation, submission, history) is delegated to useSwap.
 */

import { useState, useMemo, useCallback } from "react";
import { useAppKit } from "@reown/appkit/react";
import { useAccount, useBalance } from "wagmi";
import { TOKENS, oppositeToken, type TokenSymbol } from "@/lib/tokens";
import { formatPct, parseAmount } from "@/lib/format";
import { useQuote } from "@/hooks/useQuote";
import { useSlippage } from "@/hooks/useSlippage";
import { useSwap } from "@/hooks/useSwap";
import { TokenInput } from "./TokenInput";
import { RouteBreakdown } from "./RouteBreakdown";
import { TxStatus } from "./TxStatus";
import { SlippageSettings } from "./SlippageSettings";
import { TxHistory } from "./TxHistory";

export function SwapCard() {
  const [inputToken, setInputToken] = useState<TokenSymbol>("USDC");
  const [amount, setAmount] = useState("");
  const [slippageBps, setSlippageBps] = useSlippage();

  const outputToken = oppositeToken(inputToken);
  const inTok = TOKENS[inputToken];
  const outTok = TOKENS[outputToken];

  const { quote, loading: quoteLoading, error: quoteError } = useQuote({
    amount,
    inputToken,
  });

  const { address, isConnected } = useAccount();
  const { open } = useAppKit();
  const { data: balance } = useBalance({
    address,
    token: inTok.address,
    query: { enabled: isConnected },
  });

  const parsedAmount = parseAmount(amount, inTok.decimals);
  const hasAmount = parsedAmount !== null && parsedAmount > 0n;
  const insufficient =
    hasAmount && balance !== undefined && parsedAmount! > balance.value;

  // ── Swap lifecycle ────────────────────────────────────────────
  const {
    txStep, txError, swapTxHash, approveTxHash, submitting,
    allowance, requiresApproval, approvalState,
    doApprove, doSwap, closeTx, resetApproval,
    txHistoryEntries, clearHistory,
  } = useSwap({
    inputToken,
    outputToken,
    tokenAddress: inTok.address,
    amount,
    parsedAmount,
    slippageBps,
    expectedOut: quote?.totalOutFormatted,
  });

  const flip = () => {
    setInputToken(outputToken);
    setAmount("");
    resetApproval();
  };

  // ── Button state machine ──────────────────────────────────────
  const buttonState = useMemo(() => {
    if (!isConnected)
      return { label: "Connect wallet", disabled: false, kind: "connect" as const };
    if (!hasAmount)
      return { label: "Enter an amount", disabled: true, kind: "idle" as const };
    if (insufficient)
      return { label: "Insufficient balance", disabled: true, kind: "error" as const };
    if (submitting)
      return { label: "Confirm in wallet…", disabled: true, kind: "pending" as const };
    if (quoteLoading || !quote)
      return { label: "Fetching quote…", disabled: true, kind: "pending" as const };
    if (approvalState === "prompting" || approvalState === "pending")
      return { label: "Approving…", disabled: true, kind: "pending" as const };
    if (requiresApproval)
      return { label: `Approve ${inputToken}`, disabled: false, kind: "approve" as const };
    return {
      label: `Swap ${inputToken} for ${outputToken}`,
      disabled: false,
      kind: "ready" as const,
    };
  }, [
    isConnected, hasAmount, insufficient, submitting, quoteLoading,
    quote, inputToken, outputToken, approvalState, requiresApproval,
  ]);

  const onAction = useCallback(async () => {
    if (buttonState.kind === "connect") {
      void open({ view: "Connect" });
      return;
    }
    if (buttonState.kind === "approve") {
      await doApprove();
      return;
    }
    if (buttonState.kind === "ready" && quote) {
      await doSwap();
    }
  }, [buttonState.kind, open, doApprove, doSwap, quote]);

  const onTxClose = useCallback(() => {
    const wasSuccess = closeTx();
    if (wasSuccess) setAmount("");
  }, [closeTx]);

  // ── Render ─────────────────────────────────────────────────────

  const slippageDisplay = `${(slippageBps / 100).toFixed(2)}%`;
  const expectedOutput = quote
    ? `${quote.totalOutFormatted} ${outputToken}`
    : "Waiting for a quote";

  if (txStep !== null) {
    return (
      <section className="mx-auto w-full max-w-[460px]">
        <div className="rounded-[--radius-lg] border border-[--border] bg-[--bg-elev] p-4 shadow-[--shadow-card]">
          <TxStatus
            step={txStep}
            inputToken={inputToken}
            outputToken={outputToken}
            amountIn={amount}
            expectedOut={quote?.totalOutFormatted ?? "—"}
            txHash={swapTxHash}
            approveTxHash={approveTxHash}
            error={txError}
            onClose={onTxClose}
          />
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto w-full max-w-[460px]">
      <div className="rounded-[--radius-lg] border border-[--border] bg-[--bg-elev] p-4 shadow-[--shadow-card]">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-[15px] font-semibold tracking-tight text-[--text]">
            Swap
          </h2>
          <div className="flex items-center gap-2 text-[11px] text-[--text-subtle]">
            <SlippageSettings bps={slippageBps} onChange={setSlippageBps} />
            {quote && (
              <span className="num text-[--success]">
                uplift {formatPct(quote.splittingUpliftPct, 2)}
              </span>
            )}
          </div>
        </div>

        <TokenInput
          label="You pay"
          side="from"
          token={inTok}
          value={amount}
          onChange={setAmount}
        />

        <div className="my-2 flex justify-center">
          <button
            type="button"
            onClick={flip}
            aria-label="Flip direction"
            className="grid size-9 place-items-center rounded-full border border-[--border] bg-[--bg-elev] text-[--text-muted] hover:text-[--text] hover:border-[--border-strong] transition"
          >
            <svg
              aria-hidden="true"
              focusable="false"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              width="18"
              height="18"
            >
              <path d="M7 4v16M7 20l-3-3M7 20l3-3M17 20V4M17 4l-3 3M17 4l3 3" />
            </svg>
          </button>
        </div>

        <TokenInput
          label="You receive"
          side="to"
          token={outTok}
          value={quote?.totalOutFormatted ?? ""}
          loading={quoteLoading}
        />

        <div className="mt-3 rounded-[--radius-sm] bg-[--bg-inset] px-3 py-2 text-[12px] text-[--text-muted]">
          <div className="flex items-center justify-between gap-3">
            <span>Expected Output</span>
            <span className="num min-w-0 truncate text-right text-[--text]">
              {expectedOutput}
            </span>
          </div>
          <div className="mt-1 flex items-center justify-between gap-3">
            <span>Slippage</span>
            <span className="num text-[--text]">{slippageDisplay}</span>
          </div>
          {isConnected && hasAmount && allowance !== undefined && (
            <div className="mt-1 flex items-center justify-between gap-3">
              <span>Allowance</span>
              <span className={`num ${requiresApproval ? "text-[--danger]" : "text-[--success]"}`}>
                {requiresApproval ? "Approval needed" : "Sufficient"}
              </span>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onAction}
          disabled={buttonState.disabled}
          className={`mt-4 w-full rounded-[--radius] px-4 py-3.5 text-[15px] font-semibold transition ${
            buttonState.kind === "error"
              ? "bg-[--danger] text-white"
              : buttonState.kind === "approve" || buttonState.kind === "ready"
                ? "bg-[--accent] text-[--accent-on] hover:bg-[--accent-hover]"
                : "bg-[--bg-inset] text-[--text-subtle]"
          } disabled:cursor-not-allowed`}
        >
          {buttonState.label}
        </button>

        {quoteError && (
          <p className="mt-3 text-center text-xs text-[--danger]" role="status" aria-live="polite">
            {quoteError.message}
          </p>
        )}
      </div>

      <RouteBreakdown quote={quote} loading={quoteLoading} />
      <TxHistory entries={txHistoryEntries} onClear={clearHistory} />
    </section>
  );
}
